import { useState, useEffect, useRef } from 'react';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { X, Bug, Sparkles, HelpCircle, Heart, MoreHorizontal, Camera, Trash2 } from 'lucide-react';

const CATEGORIES = [
  { id: 'bug', label: 'Bug', icon: Bug, tone: 'text-red-700 bg-red-50 border-red-200' },
  { id: 'feature', label: 'Feature Request', icon: Sparkles, tone: 'text-gold bg-gold/10 border-gold/30' },
  { id: 'confusing', label: 'Something Confusing', icon: HelpCircle, tone: 'text-navy bg-beige border-navy/10' },
  { id: 'love', label: 'I Love This', icon: Heart, tone: 'text-green bg-green/10 border-green/20' },
  { id: 'other', label: 'Other', icon: MoreHorizontal, tone: 'text-navy bg-beige border-navy/10' },
];

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('feature');

  // Allow any page to open the feedback modal via a global event
  // (used by the Help Center "Send feedback" button).
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('bp:open-feedback', onOpen);
    return () => window.removeEventListener('bp:open-feedback', onOpen);
  }, []);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState(null); // base64
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedRef, setSubmittedRef] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [submitterEmail, setSubmitterEmail] = useState(null);
  const fileRef = useRef(null);

  // Reset when closed
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setSubmitted(false);
        setSubmittedRef(null);
        setEmailSent(false);
        setSubmitterEmail(null);
        setSubject('');
        setMessage('');
        setScreenshot(null);
        setCategory('feature');
      }, 300);
    }
  }, [open]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3_000_000) { toast.error('Screenshot too large (max 3 MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => setScreenshot(String(reader.result));
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!message.trim()) { toast.error('Add a short message'); return; }
    setSubmitting(true);
    try {
      const r = await http.post('/feedback', {
        category,
        subject: subject.trim() || undefined,
        message: message.trim(),
        page_url: window.location.pathname,
        screenshot_base64: screenshot || undefined,
      });
      setSubmittedRef(r.data?.ref || r.data?.feedback?.ref || null);
      setEmailSent(!!r.data?.email_sent);
      setSubmitterEmail(r.data?.feedback?.user_email || null);
      setSubmitted(true);
      toast.success('Thanks for the signal.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating trigger removed — feedback now opens from Profile → Support & help
          via the `bp:open-feedback` event handled above. */}

      {open && (
        <div className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm flex items-end lg:items-center justify-center p-0 lg:p-6" onClick={() => setOpen(false)}>
          <div
            data-testid="feedback-modal"
            onClick={(e) => e.stopPropagation()}
            className="w-full lg:max-w-lg bg-white rounded-t-3xl lg:rounded-3xl p-6 lg:p-8 shadow-2xl animate-fade-up"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Feedback</p>
                <h2 className="font-heading text-2xl lg:text-3xl text-navy mt-1">Tell us what you&rsquo;re noticing.</h2>
              </div>
              <button data-testid="feedback-close" onClick={() => setOpen(false)} className="text-navy/40 hover:text-navy p-1"><X size={20}/></button>
            </div>

            {submitted ? (
              <div data-testid="feedback-thanks" className="text-center py-8">
                <div className="w-14 h-14 mx-auto rounded-full bg-green/10 flex items-center justify-center mb-3">
                  <Heart className="text-green" size={26} strokeWidth={1.5}/>
                </div>
                <p className="font-heading text-xl text-navy">Thank you. Your feedback has been received.</p>
                {submittedRef && (
                  <p className="text-sm text-navy/70 mt-2" data-testid="feedback-ref">
                    Reference: <span className="font-mono font-semibold text-navy">{submittedRef}</span>
                  </p>
                )}
                <p className="text-sm text-navy/60 mt-1">We read every submission. Your input shapes this.</p>
                {emailSent && submitterEmail && (
                  <p className="text-xs text-navy/55 mt-3" data-testid="feedback-email-confirmed">
                    A confirmation email has been sent to <span className="font-medium">{submitterEmail}</span>.
                  </p>
                )}
                <button onClick={() => setOpen(false)} className="mt-6 bg-navy text-white rounded-full px-6 py-3 text-sm" data-testid="feedback-thanks-close">Close</button>
              </div>
            ) : (
              <>
                <p className="text-xs uppercase tracking-widest text-navy/50 font-semibold mb-2">Category</p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c.id}
                      data-testid={`feedback-cat-${c.id}`}
                      onClick={() => setCategory(c.id)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-2xl text-sm text-left border transition-smooth ${
                        category === c.id ? c.tone : 'bg-white border-navy/10 text-navy/70 hover:border-navy/30'
                      }`}
                    >
                      <c.icon size={14}/>{c.label}
                    </button>
                  ))}
                </div>

                <p className="text-xs uppercase tracking-widest text-navy/50 font-semibold mb-2">Subject <span className="text-navy/30 normal-case tracking-normal font-normal">(optional)</span></p>
                <input
                  data-testid="feedback-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Short summary (e.g. 'Journal save button not working')"
                  maxLength={200}
                  className="w-full bg-white border border-navy/10 rounded-2xl px-4 py-3 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-gold mb-3"
                />

                <p className="text-xs uppercase tracking-widest text-navy/50 font-semibold mb-2">Your message</p>
                <textarea
                  data-testid="feedback-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={category === 'bug' ? "What went wrong? What did you expect?" : category === 'feature' ? "What would you love to see?" : category === 'confusing' ? "What confused you?" : category === 'love' ? "What are you loving?" : "Tell us what's on your mind…"}
                  rows={5}
                  maxLength={2000}
                  className="w-full bg-white border border-navy/10 rounded-2xl p-4 text-sm text-navy leading-relaxed focus:outline-none focus:ring-2 focus:ring-gold resize-none"
                />

                <div className="mt-3 flex items-center justify-between gap-3">
                  <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="feedback-file-input"/>
                  {!screenshot ? (
                    <button data-testid="feedback-add-screenshot" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 text-xs bg-beige text-navy rounded-full px-3 py-2 hover:bg-[#EAE5D9]">
                      <Camera size={12}/>Add screenshot (optional)
                    </button>
                  ) : (
                    <div className="inline-flex items-center gap-2 text-xs">
                      <img src={screenshot} alt="Attached screenshot" className="w-10 h-10 rounded-lg object-cover"/>
                      <button data-testid="feedback-remove-screenshot" onClick={() => setScreenshot(null)} className="text-red-600 hover:text-red-800 inline-flex items-center gap-1"><Trash2 size={12}/>Remove</button>
                    </div>
                  )}
                  <span className="text-[10px] text-navy/40 hidden lg:inline">On page: {window.location.pathname}</span>
                </div>

                <button
                  data-testid="feedback-submit"
                  onClick={submit}
                  disabled={submitting || !message.trim()}
                  className="mt-5 w-full bg-navy text-white rounded-full py-3 lg:py-4 text-sm font-medium hover:bg-navy-soft transition-smooth disabled:opacity-50"
                >
                  {submitting ? 'Sending…' : 'Send feedback'}
                </button>
                <p className="text-[10px] text-navy/40 mt-3 text-center leading-relaxed">
                  We capture your page + device automatically to help us reproduce issues. Nothing else is sent.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
